import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router';
import axiosInstance from '../components/axios';

// API/Components
import { AnalysisResultToStateColor, measUnitsConversion, measUnitsArrayConversion, userMetricUnits } from '../components/enums';

// Material UI
import Button from '@mui/material/Button';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import Grid from '@mui/material/Grid';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import {
	Chart as ChartJS,
	LinearScale,
	CategoryScale,
	BarElement,
	PointElement,
	LineElement,
	Legend,
	Tooltip,
	BarController,
	ScatterController,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import zoomPlugin from 'chartjs-plugin-zoom';
import tinycolor from "tinycolor2";

// Register Chart.JS Plugins
ChartJS.register(
	LinearScale,
	CategoryScale,
	BarElement,
	PointElement,
	LineElement,
	Legend,
	Tooltip,
	BarController,
	ScatterController,
	zoomPlugin,
);


export default function UnitDischargeSnapshotGraph(props) {
	const { snapshotData, selectedString, numUnits, graphType } = props;
	const { locationNum } = useParams();
	
	const [graphData, setGraphData] = useState([]);
	const [graphSettings, setGraphSettings] = useState({
		limitsEnabled: true,
		maxminEnabled: true,
		avgEnabled: false,
	});
	
	const [limitData, setLimitData] = useState([]);
	const [graphOptions, setGraphOptions] = useState({
		maintainAspectRatio: false,
		animation: false,
		options: {
			responsive: true,
		},
		transitions: {
			zoom: {
				animation: {
					duration: 0,
				},
			},
		},
		plugins: {
			legend: {
				display: false,
			},
			tooltip: {
				intersect: false,
				// axis: 'x',
				mode: 'index',
			},
			zoom: {
				pan: {
					enabled: true,
					mode: 'xy',
				},
				limits: {
					x: {
						minRange: 2,
					},
					y: {
						min: 0,
						max: 100,
						minRange: 5,
					}
				},
				zoom: {
					mode: 'y',
					wheel: {
						enabled: true,
						speed: 0.02,
					},
					pinch: {
						enabled: true,
					},
					onZoomStart: (e) => {
						if (typeof e.chart.config?.options?.plugins?.zoom?.zoom === "undefined") {
							return;
						}
						if (e.event.shiftKey === true) {
							e.chart.config.options.plugins.zoom.zoom.mode = "x";
							e.chart.config.options.plugins.zoom.zoom.scaleMode = "x";
						} else {
							e.chart.config.options.plugins.zoom.zoom.mode = "y";
							e.chart.config.options.plugins.zoom.zoom.scaleMode = "y";
						}
					},
				}
			},
		},
	});
	
	// const [isLoading, setIsLoading] = useState(loading);
	
	const chartRef = useRef(null);

	// Determine if the user is using metric or imperial units
	const tempMeasurementUnits = userMetricUnits() ? '°C' : '°F';

	const graphPlugins = [{
		afterDraw: chart => {
			if (chart.tooltip?._active?.length) {
				let x = chart.tooltip._active[0].element.x;
				let yAxis = chart.scales.y;
				let ctx = chart.ctx;
				ctx.save();
				ctx.beginPath();
				ctx.setLineDash([5, 5]);
				ctx.moveTo(x, yAxis.top);
				ctx.lineTo(x, yAxis.bottom);
				ctx.lineWidth = 1;
				ctx.strokeStyle = 'rgba(0, 0, 255, 0.9)';
				ctx.stroke();
				ctx.restore(); 
			}
		}
	}];

	useEffect(() => {
		if (typeof snapshotData != "undefined" && snapshotData !== null) {

			const unitsData = GetGraphData(snapshotData, selectedString, numUnits, graphType);
			if (unitsData !== undefined && unitsData !== null) {
				setGraphData(unitsData);
			}
		}
	}, [numUnits, snapshotData, selectedString, graphType, graphSettings, limitData]);

	useEffect(() => {
		axiosInstance.get(`/loc/${locationNum}/limits_flat/discharge/unit`).then(response => {
			const limitsInfo = response.data;
			setLimitData(limitsInfo);
		});
	}, [locationNum]);

	useEffect(() => {
		const graphScales = SetGraphYScale(selectedString, graphType);
		if (graphScales !== undefined || graphScales !== null) {
			setGraphOptions((prevState) => {
				return {
					...prevState,
					scales: {
						y: graphScales,
						x: {
							title: {
								display: true,
								text: "Units",
								font: {
									family: "Saira, sans-serif",
									weight: 600,
									size: 18,
								},
							}
						}
					}
				}
			});
		}
	}, [limitData, graphType, selectedString]);
	
	useEffect(() => {
		const graphZoom = SetGraphZoom(graphType);
		if (graphZoom !== undefined || graphZoom !== null) {
			setGraphOptions((prevState) => {
				return {
					...prevState,
					plugins: {
						...prevState.plugins,
						zoom: {
							...prevState.plugins.zoom,
							limits: {
								...prevState.plugins.zoom.limits,
								y: graphZoom,
							},
						},
					},
				}
			});
		}
	}, [limitData, graphType]);


	function GetMaxAvgMinData(stringStats, selectedString, numUnits, graphType) {
		let maxValues = [];
		let avgValues = [];
		let minValues = [];
		
		if (stringStats !== undefined && stringStats !== null) {
			stringStats?.forEach(string => {
				if (selectedString == 0 || string.string_nbr == selectedString) {
					let max = 0;
					let avg = 0;
					let min = 0;

					switch(graphType) {
						case 0:
							max = string.voltage.max;
							avg = string.voltage.avg;
							min = string.voltage.min;
							break;
						case 1:
							max = measUnitsConversion(string.temperature.max);
							avg = measUnitsConversion(string.temperature.avg);
							min = measUnitsConversion(string.temperature.min);
							break;
						default:
							max = string.voltage.max;
							avg = string.voltage.avg;
							min = string.voltage.min;
							break;
					}

					// Push the max/avg/min values to the appropriate arrays
					//  for the number of units in the string
					for (let i = 0; i < numUnits; i++) {
						maxValues.push(max);
						avgValues.push(avg);
						minValues.push(min);
					}
				}
			});
		}
		
		// Get the associated limit colors
		let maxColor = '#0033FF';
		let avgColor = '#005500';
		let minColor = '#0033FF';

		let statSets = {
			'maxSet': {},
			'avgSet': {},
			'minSet': {}
		};
		if (maxValues !== undefined && maxValues.length > 0) {
			statSets.maxSet = {
				type: 'scatter',
				label: 'String Maximum',
				backgroundColor: maxColor + "55",
				borderColor: maxColor + "77",
				borderWidth: 1,
				pointStyle: 'triangle',
				radius: 4,
				rotation: 180,
				hitRadius: 5,
				data: maxValues,
				order: 4,
			};
		}
		
		if (avgValues !== undefined && avgValues.length > 0) {
			statSets.avgSet = {
				type: 'scatter',
				label: 'String Average',
				backgroundColor: avgColor + "55",
				borderColor: avgColor + "77",
				borderWidth: 1,
				pointStyle: 'crossRot',
				radius: 5,
				hitRadius: 5,
				data: avgValues,
				order: 5,
			};
		}

		if (minValues !== undefined && minValues.length > 0) {
			statSets.minSet = {
				type: 'scatter',
				label: 'String Minimum',
				backgroundColor: minColor + "55",
				borderColor: minColor + "77",
				borderWidth: 1,
				pointStyle: 'triangle',
				radius: 4,
				rotation: 0,
				hitRadius: 5,
				data: minValues,
				order: 6,
			};
		}

		return statSets;
	}

	function GetLimitsData(selectedString, graphType) {
		let critHiLimits = [];
		let maintLoLimits = [];
		let critLoLimits = [];
		
		if (limitData !== undefined && limitData !== null) {
			limitData?.forEach(string => {
				if (selectedString == 0 || string.string_nbr == selectedString) {
					switch(graphType) {
						case 0:
							Array.prototype.push.apply(maintLoLimits, string.volt.lo_maint);
							Array.prototype.push.apply(critLoLimits, string.volt.lo_crit);
							break;
						case 1:
							Array.prototype.push.apply(critHiLimits, measUnitsArrayConversion(string.temp.hi));
							Array.prototype.push.apply(critLoLimits, measUnitsArrayConversion(string.temp.lo));
							break;
						default:
							Array.prototype.push.apply(maintLoLimits, string.volt.lo_maint);
							Array.prototype.push.apply(critLoLimits, string.volt.lo_crit);
							break;
					}
				}
			});
		}

		// Get the associated limit colors
		let hiCritColor = AnalysisResultToStateColor(4);
		let loMaintColor = AnalysisResultToStateColor(3);
		let loCritColor = AnalysisResultToStateColor(5);

		let limitSets = [];
		if (critHiLimits !== undefined && critHiLimits.length > 0) {
			limitSets.push({
				type: 'scatter',
				label: 'Upper Critical Limit',
				backgroundColor: hiCritColor + "77",
				borderColor: hiCritColor,
				borderWidth: 1,
				pointStyle: 'rect',
				radius: 4,
				hitRadius: 5,
				data: critHiLimits,
				order: 0,
			});
		}

		if (maintLoLimits !== undefined && maintLoLimits.length > 0) {
			limitSets.push({
				type: 'scatter',
				label: 'Lower Maintenance Limit',
				backgroundColor: loMaintColor + "77",
				borderColor: loMaintColor,
				borderWidth: 1,
				pointStyle: 'rect',
				radius: 4,
				hitRadius: 5,
				data: maintLoLimits,
				order: 0,
			});
		}

		if (critLoLimits !== undefined && critLoLimits.length > 0) {
			limitSets.push({
				type: 'scatter',
				label: 'Lower Critical Limit',
				backgroundColor: loCritColor + "77",
				borderColor: loCritColor,
				borderWidth: 1,
				pointStyle: 'rect',
				radius: 4,
				hitRadius: 5,
				data: critLoLimits,
				order: 0,
			});
		}
		
		return limitSets;
	}

	function SetGraphYScale(selectedString, graphType) {
		
		if (limitData !== undefined && limitData !== null && limitData.length > 0) {

			let max = 0;
			let min = 50;
			let title = "";

			switch (graphType) {
				case 0:  // Voltage
					limitData.forEach((string) => {
						if (selectedString == 0 || string.string_nbr == selectedString) {
							let strMax = Math.max(...string.volt.lo_maint) * 1.5;
							if (strMax > max) { max = strMax; }
							
							let strMin = Math.min(...string.volt.lo_crit) * 0.80;
							if (strMin < min) { min = strMin; }
						}
					});
					title = "Unit Discharge Voltage (VDC)";
					break;

				case 1:  // Temperature
					let hiTemp = limitData[0].temp.hi[0]
					let loTemp = limitData[0].temp.lo[0]
					max = 1.15 * measUnitsConversion(hiTemp);
					min = 0.85 * measUnitsConversion(loTemp);
					title = 'Unit Discharge Temperature (' + tempMeasurementUnits + ')';
					break;
				
				default:
					limitData.forEach((string) => {
						if (selectedString == 0 || string.string_nbr == selectedString) {
							let strMax = Math.max(...string.volt.lo_maint) * 1.5;
							if (strMax > max) { max = strMax; }
							
							let strMin = Math.min(...string.volt.lo_crit) * 0.80;
							if (strMin < min) { min = strMin; }
						}
					});
					title = "Unit Discharge Voltage (VDC)";
					break;				
			}

			if (max > 0 && min != 50) {
				return {
					max: max,
					min: min,
					title: {
						display: true,
						text: title,
						font: {
							family: "Saira, sans-serif",
							size: 18,
							weight: 600,
						},
					},
				};
			}
		}

		return {};
	}
	
	function SetGraphZoom(graphType) {

		switch(graphType) {
			case 0:  // Voltage
				return {
					min: 0,
					max: 30,
					minRange: 0.5,
				};
			case 1:  // Temperature
				return {
					min: measUnitsConversion(-40),
					max: measUnitsConversion(75),
					minRange: 5,
				};

			default:
				return {
					min: 0,
					max: 100,
					minRange: 5,
				};
		}
	}
	
	function GetUnitInfo(unit, graphType) {
		let value, analysis = 0;
		switch(graphType) {
			case 0:
				value = unit.voltage.measurement?.toFixed(3);
				analysis = (unit.voltage.analysis_valid) ? 
					unit.voltage.analysis_result :	0;
				break;
			case 1:
				let temp = unit.temperature.measurement?.toFixed(1);
				value = measUnitsConversion(temp);
				analysis = (unit.temperature.analysis_valid) ? 
					unit.temperature.analysis_result : 0;
				break;
			default:
				value = unit.voltage.measurement?.toFixed(3);
				analysis = (unit.voltage.analysis_valid) ? 
					unit.voltage.analysis_result :	0;
			break;
		}
		value = (value !== null && value !== undefined && value > -4000000) ? value : 0;
		let analysisColor = AnalysisResultToStateColor(analysis);

		let color = unit.unit_replaced ? tinycolor(analysisColor).darken(15).toString() : analysisColor;

		return {value, color};
	}

	function GetGraphTypeText(graphType) {
		switch(graphType) {
			case 0:
				return "Unit Discharge Voltage Snapshot";
			case 1:
				return "Unit Discharge Temperature Snapshot";
			default:
				return "Unit Discharge Voltage Snapshot";
		}
	}
	
	
	function GetGraphData(snapshotData, selectedString, numUnits, graphType) {
		// Pick the units to graph based on the selected string
		const unitData = snapshotData?.units.filter((x) => (selectedString == 0 || x.string_nbr == selectedString));
		if (unitData && unitData.length > 0) {
			
			const filteredData = unitData.map((unit) => {
				const { value, color } = GetUnitInfo(unit, graphType);
				
				return {
					label: (selectedString == 0) ? `S${unit.string_nbr} U${unit.unit_nbr}` : `Unit ${unit.unit_nbr}`,
					value: value,
					barColor: color + "77",  // Make the bar color partially transparent
					borderColor: color,  // The outline should be fully opaque
				};
			});

			const graphTypeText = GetGraphTypeText(graphType);
			
			// Add measurement data to datasets
			let dataSets = []
			dataSets.push({
				type: 'bar',
				label: graphTypeText,
				backgroundColor: filteredData.map((unit) => unit.barColor),
				borderColor: filteredData.map((unit) => unit.borderColor),
				borderWidth: 2,
				data: filteredData.map((unit) => unit.value),
				order: 10,
			});

			// check for limits enabled and add limitsets to datasets if enabled
			if (graphSettings.limitsEnabled) {
				const limitsData = GetLimitsData(selectedString, graphType);
				if (limitsData !== undefined && limitsData.length > 0) {
					limitsData.forEach((limitSet) => {
						dataSets.push(limitSet);
					});
				}
			}
			
			// check for limits enabled and add limitsets to datasets if enabled
			const stringStats = snapshotData?.strings;
			if (graphSettings.maxminEnabled || graphSettings.avgEnabled) {
				const maxavgminData = GetMaxAvgMinData(stringStats, selectedString, numUnits, graphType);
				if (maxavgminData !== undefined) {
					if (graphSettings.maxminEnabled) {
						if (maxavgminData.maxSet !== undefined) { dataSets.push(maxavgminData.maxSet); }
						if (maxavgminData.minSet !== undefined) { dataSets.push(maxavgminData.minSet); }
					}

					if (graphSettings.avgEnabled && maxavgminData.minSet !== undefined && maxavgminData.minSet != {}) {
						dataSets.push(maxavgminData.avgSet);
					}
				}
			}

			return {
				labels: filteredData.map((unit) => unit.label),
				datasets: dataSets,
			};
		}

		return null;
	}

	const handleLimitMarkersToggle = () => {
		setGraphSettings((prevState) => ({
			...prevState,
			limitsEnabled: !graphSettings.limitsEnabled})
		);
	};

	const handleMaxMinMarkersToggle = () => {
		setGraphSettings((prevState) => ({
			...prevState,
			maxminEnabled: !graphSettings.maxminEnabled})
		);
	};

	const handleAvgMarkersToggle = () => {
		setGraphSettings((prevState) => ({
			...prevState,
			avgEnabled: !graphSettings.avgEnabled})
		);
	};

	const handleGraphResetZoom = () => {
		chartRef.current.resetZoom();
	};

	return (
		<div
			style={{
				width: '100%',
				height: '99%',
				display: 'flex',
				flexDirection: 'column',
				position: 'relative',
			}}
		>
			<div style={{ height: '100%', width: '100%' }}>
				{ (graphData !== undefined && graphData.length != 0) &&
					<Chart
						ref={chartRef}
						type='bar'
						options={graphOptions}
						data={graphData}
						plugins = {graphPlugins}
					/>
				}
			</div>
			<Grid container spacing={1}>
				<Grid item xs alignItems="left">
					<ToggleButtonGroup
						value={[]}
						onChange={() => {}}
						aria-label="graph markers shown"
						size='small'
						sx={{ padding: '6px' }}
					>
						<ToggleButton
							value="limitMarkers"
							selected={graphSettings.limitsEnabled}
							onClick={handleLimitMarkersToggle}
						>
							Limits
						</ToggleButton>
						<ToggleButton
							value="maxminMarkers"
							selected={graphSettings.maxminEnabled}
							onClick={handleMaxMinMarkersToggle}
						>
							Max/Min
						</ToggleButton>
						<ToggleButton
							value="avgMarkers"
							selected={graphSettings.avgEnabled}
							onClick={handleAvgMarkersToggle}
						>
							Avg
						</ToggleButton>
					</ToggleButtonGroup>
				</Grid>
				<Grid item alignItems="right">
					<Button
						variant="contained"
						startIcon={<CenterFocusStrongIcon />}
						onClick={handleGraphResetZoom}
						sx={{
							marginY: 1,
							backgroundColor: 'white',
							border: '1px solid lightgray',
							color: 'black',
							":hover": {
								backgroundColor: 'lightgray',
								boxShadow: 'none',
							}

						}}
					>
						Reset View
					</Button>
				</Grid>
			</Grid>
		</div>
	);
}